/** How much of `star` (1–5) should be filled for `value`. 0 empty, 1 full, 0.5 half. */
export function ratingStarFraction(star: number, value: number | null | undefined): number {
  const rating = Number(value);
  if (!Number.isFinite(rating) || rating <= star - 1) {
    return 0;
  }
  if (rating >= star) {
    return 1;
  }
  return rating - (star - 1);
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function readCount(source: Record<string, unknown>, keys: string[]): number | null {
  for (const key of keys) {
    const raw = source[key];
    if (raw == null || raw === '') continue;
    const numeric = Number(raw);
    if (Number.isFinite(numeric)) return numeric;
  }
  return null;
}

const COUNT_KEYS = ['totalCount', 'total', 'totalRecords', 'totalElements', 'count'];

/**
 * Row count for a feedback list response.
 * Reads paginationInfo.totalCount and the same count under the other keys this API has used.
 */
export function feedbackListTotal(response: unknown, fallback: number): number {
  const root = asRecord(response);
  const wrapped = asRecord(root['data']);
  const record =
    wrapped['paginationInfo'] || wrapped['result'] || Array.isArray(wrapped['data']) ? wrapped : root;
  const nestedResult = asRecord(record['result']);
  const paginationInfo = asRecord(nestedResult['paginationInfo'] ?? record['paginationInfo']);
  const pagination = asRecord(nestedResult['pagination'] ?? record['pagination']);

  const counted =
    readCount(paginationInfo, COUNT_KEYS) ??
    readCount(pagination, COUNT_KEYS) ??
    readCount(nestedResult, ['totalCount']) ??
    readCount(record, ['totalCount', 'total']);
  if (counted != null) return counted;

  const pages = Number(paginationInfo['totalPages'] ?? pagination['totalPages']);
  const size = Number(
    paginationInfo['size'] ?? paginationInfo['pageSize'] ?? pagination['size'] ?? pagination['pageSize'],
  );
  if (Number.isFinite(pages) && pages > 1 && Number.isFinite(size) && size > 0) {
    return (pages - 1) * size + 1;
  }
  return fallback;
}

const REJECTION_KEYS = [
  'rejectionComment',
  'rejectComment',
  'rejectionReason',
  'statusComment',
  'adminComment',
  'reviewerComment',
  'moderationComment',
  'remarks',
  'reason',
];

function readString(source: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

/** Admin rejection note. `feedbackComment` is the field returned with `feedbackStatus`. */
export function feedbackRejectionComment(
  source: Record<string, unknown>,
  userFeedback: string,
  status: string,
): string {
  if (status !== 'rejected') {
    return '';
  }
  const feedbackComment = readString(source, ['feedbackComment']);
  if (feedbackComment) {
    return feedbackComment;
  }
  const subdata = asRecord(source['actionSubdata']);
  const decision = asRecord(source['decision'] ?? source['statusDetail'] ?? source['moderation']);
  const text =
    readString(source, REJECTION_KEYS) ||
    readString(subdata, REJECTION_KEYS) ||
    readString(decision, ['comment', ...REJECTION_KEYS]);
  const own = userFeedback.trim();
  if (text && text !== own && text !== '—') {
    return text;
  }
  const rootComment = readString(source, ['comment']);
  if (rootComment && rootComment !== own && rootComment !== '—') {
    return rootComment;
  }
  return '';
}
